import { Icon } from 'components/Icon';
import { tagManagementStrings } from 'constants/strings';
import { resolveTagColorClasses } from 'constants/tagColors';
import type { TLineFriendTag } from 'types';
import { cn } from 'utils/cn';

type TagSelectorProps = {
  tags: readonly TLineFriendTag[];
  selectedTagIds: number[];
  onToggle: (tagId: number) => void;
  disabled?: boolean;
};

/**
 * 選択状態を色の濃淡だけで示すと判別できない環境があるため、アイコン・ベタ塗り反転・
 * 件数表示を併記する（WCAG 1.4.1「色の使用」/ issue #337）。
 *
 * ローディング・エラー・タグ0件の出し分けは画面ごとに文言が異なるため呼び出し側の責務。
 */
export function TagSelector({
  tags,
  selectedTagIds,
  onToggle,
  disabled = false,
}: TagSelectorProps) {
  // selectedTagIds には削除済みタグのIDが残りうるため、表示中のタグに限って数える。
  const selectedCount = tags.filter((tag) =>
    selectedTagIds.includes(tag.id),
  ).length;

  return (
    <div className="flex flex-col gap-2">
      <p
        className={cn(
          'text-[11px] font-semibold',
          selectedCount > 0 ? 'text-accent' : 'text-muted',
        )}
      >
        {selectedCount > 0
          ? tagManagementStrings.selector.selectedCount(selectedCount)
          : tagManagementStrings.selector.noneSelected}
      </p>

      <div className="flex flex-wrap gap-2">
        {tags.map((tag) => {
          const checked = selectedTagIds.includes(tag.id);
          const classes = resolveTagColorClasses(tag.color);

          return (
            <label
              key={tag.id}
              data-testid={`tag-option-${tag.id}`}
              className={cn(
                'inline-flex cursor-pointer items-center gap-1.5 rounded-full border-2 px-2.5 py-1 text-xs font-semibold',
                // 背景色と文字色は遷移させない。両方を同時にフェードさせると、
                // 切り替え途中で「タグ色の地にタグ色の文字」になり一瞬読めなくなるため。
                'transition-[border-color,box-shadow]',
                // チェックボックスが sr-only のため、キーボードフォーカスはラベル側で可視化する。
                'focus-within:ring-2 focus-within:ring-accent focus-within:ring-offset-1',
                checked
                  ? cn('border-transparent text-white shadow-sm', classes.solidBg)
                  : cn(
                      'hover:border-ink/40',
                      classes.bg,
                      classes.border,
                      classes.text,
                    ),
                disabled && 'cursor-not-allowed opacity-60',
              )}
            >
              <input
                type="checkbox"
                className="sr-only"
                checked={checked}
                disabled={disabled}
                onChange={() => onToggle(tag.id)}
              />
              {checked ? (
                <span
                  data-testid="tag-check-icon"
                  className="inline-flex shrink-0"
                >
                  <Icon
                    icon="checkBold"
                    width={12}
                    height={12}
                    className="shrink-0"
                  />
                </span>
              ) : (
                <span
                  aria-hidden="true"
                  className="h-3 w-3 shrink-0 rounded-full border-[1.5px] border-current opacity-50"
                />
              )}
              <span>{tag.name}</span>
            </label>
          );
        })}
      </div>
    </div>
  );
}
